import React from 'react';
import { FooterLinkSection, NavLink, TeamUser } from '../types';
import { IS_DEV } from 'src/constants';
import {
  LongTermMarketingContent,
  MarketingAuditContent,
  GA4ToMMMContent,
  StandardizingMediaDataContent,
  RobynVsGoogleMeridianVsYapamarContent,
  WhyMMMMustBecomeDefaultContent,
} from '../components/molecules/BlogContent';

import SolutionImage1 from 'src/assets/images/fetures/mmm/mmm_overview.jpg';
import SolutionImage2 from 'src/assets/images/fetures/mmm/mmm_summary.jpg';
import SolutionImage3 from 'src/assets/images/fetures/mmm/mmm_trends.jpg';
import SolutionImage4 from 'src/assets/images/fetures/mmm/mmm_modeling.jpg';

import ProductImage1 from 'src/assets/images/product/main.jpg';
import ProductImage2 from 'src/assets/images/product/product-2.jpg';
import ProductImage3 from 'src/assets/images/product/product-3.jpg';
import ProductImage4 from 'src/assets/images/product/product-4.jpg';

// Getting Started Guide Images
import createAccountScreenshot from 'src/assets/images/create-account-screenshot.png';
import connectChannelsScreenshot from 'src/assets/images/connect-channels-screenshot.png';
import uploadDataScreenshot from 'src/assets/images/upload-data-screenshot.png';
import campaignMappingScreenshot from 'src/assets/images/campaign-mapping-screenshot.png';
import startModelingScreenshot from 'src/assets/images/start-modeling-screenshot.png';
import inviteFirstMemberScreenshot from 'src/assets/images/invite-first-member-screenshot.png';
import inviteNextMemberScreenshot from 'src/assets/images/invite-next-member-screenshot.png';
import createFirstGoalScreenshot from 'src/assets/images/create-first-goal-screenshot.png';
import goalsAndTargetsScreenshot from 'src/assets/images/goals-and-targets-screenshot.png';
import goalDetailsScreenshot from 'src/assets/images/goal-details-screenshot.png';
import manageProfileAndAccountSecurityScreenshot from 'src/assets/images/manage-profile-and-account-security-screenshot.png';
import billingAndSubscriptionManagementScreenshot from 'src/assets/images/billing-and-subscription-management-screenshot.png';
import goalSetupAndTrackingScreenshot from 'src/assets/images/goal-setup-and-tracking-screenshot.png';
import manageConnectedPlatformsScreenshot from 'src/assets/images/manage-connected-platforms-screenshot.png';
import notificationsSettingsScreenshot from 'src/assets/images/notifications-settings-screenshot.png';
import integrationsManagementScreenshot from 'src/assets/images/integrations-management-screenshot.png';
import aiInsightsDashboardScreenshot from 'src/assets/images/ai-insights-dashboard-screenshot.png';

import ArticleBgOne from '../assets/article-bg-one.webp';
import ArticleBgTwo from '../assets/article-bg-two.webp';
import ArticleBgThree from '../assets/article-bg-three.webp';
import ArticleBgFour from '../assets/article-bg-four.webp';
import ArticleBgFive from '../assets/article-bg-five.webp';
import ArticleBgSix from '../assets/article-bg-six.webp';

export const NAV_LINKS: NavLink[] = [
  { label: 'Home', href: '/' },
  { label: 'Product', href: '/product' },
  { label: 'Pricing', href: '/pricing' },
  // { label: 'Resources', href: '/resources' },
  // { label: 'Blog', href: '/blog' },
  { label: 'Contact Sales', href: '/contact' },
];

export const FOOTER_LINKS: FooterLinkSection[] = [
  {
    heading: 'Company',
    links: [
      { label: 'Product', href: '/product' },
      { label: 'Pricing', href: '/pricing' },
      // { label: 'Resources', href: '/resources' },
      // { label: 'Blog', href: '/blog' },
      { label: 'Contact Sales', href: '/contact' },
      // { label: 'About', href: '/company' },
    ],
  },
  {
    heading: 'Legal',
    links: [
      ...(IS_DEV ? [{ label: 'Terms of Service', href: '/terms' }] : []),
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: 'Cookie Policy', href: '/cookie-policy' },
      ...(IS_DEV
        ? [{ label: 'Service Description', href: '/service-description' }]
        : []),
      ...(IS_DEV ? [{ label: 'Subprocessors', href: '/subprocessors' }] : []),
      ...(IS_DEV ? [{ label: 'DPA', href: '/dpa' }] : []),
      ...(IS_DEV
        ? [{ label: 'Terms of Exchange', href: '/terms-of-exchange' }]
        : []),
      ...(IS_DEV ? [{ label: 'SaaS Terms', href: '/saas-terms' }] : []),
      ...(IS_DEV ? [{ label: 'User Terms', href: '/user-terms' }] : []),
    ],
  },
];

export const USERS: TeamUser[] = [
  {
    name: 'Julia Vasilieva',
    title: 'Co-Founder, COO',
    img: 'https://www.gravatar.com/avatar/?d=mp',
  },
];

export const SOLUTION_IMAGES = {
  SolutionImage1,
  SolutionImage2,
  SolutionImage3,
  SolutionImage4,
};

export const PRODUCT_IMAGES = {
  ProductImage1,
  ProductImage2,
  ProductImage3,
  ProductImage4,
};

// Blog post data structure
export interface BlogPost {
  id: number;
  slug: string;
  title: string;
  description: string;
  author: string;
  date: string;
  image: string;
  content: string | React.ComponentType;
}

// Full blog posts data
export const blogPosts: BlogPost[] = [
  {
    id: 1,
    slug: 'long-term-marketing-effectiveness',
    title: "Why Short-Term Marketing Gains Don't Build Sustainable Growth",
    description:
      'In an era obsessed with instant metrics and rapid feedback loops, marketers often prioritize short-term returns such as clicks, conversions, and immediate revenue. But as honesty demands, this myopic focus on the short term can undermine brand strength and long-term growth.',
    author: 'Julia Vasilieva',
    date: 'Aug 14, 2025',
    image: ArticleBgOne,
    content: LongTermMarketingContent,
  },
  {
    id: 2,
    slug: 'marketing-growth-audit',
    title: 'Why Every Business Leader Should Audit Their Marketing',
    description:
      "In today's economy, marketing is under the same scrutiny as every other investment. Budgets are tighter, growth targets remain high, and every euro spent must prove its value on the bottom line.",
    author: 'Julia Vasilieva',
    date: 'Sep 30, 2025',
    image: ArticleBgTwo,
    content: MarketingAuditContent,
  },
  {
    id: 3,
    slug: 'ga4-to-mmm-in-under-90-minutes',
    title: 'GA4 to MMM in Under 90 Minutes',
    description:
      "In today's fast-paced marketing world, insights delayed are opportunities lost. What if you could turn your GA4 setup into a full-blown, ML-driven Marketing Mix Model in under 90 minutes?",
    author: 'Julia Vasilieva',
    date: 'Oct 6, 2025',
    image: ArticleBgThree,
    content: GA4ToMMMContent,
  },
  {
    id: 4,
    slug: 'standardizing-media-data-for-mmm',
    title: 'Standardizing Media Data for MMM',
    description:
      'In a multi-channel world, media data comes in different shapes, frequencies, and currencies. Traditional MMM setup can drag on for weeks in data prep.',
    author: 'Julia Vasilieva',
    date: 'Oct 10, 2025',
    image: ArticleBgFour,
    content: StandardizingMediaDataContent,
  },
  {
    id: 5,
    slug: 'robyn-vs-google-meridian-vs-yapamar',
    title:
      'Robyn vs. Google Meridian vs. Yapamar - Which MMM Approach Matches Your Team?',
    description:
      'Open-source tools like Robyn (Meta) and Google Meridian offer powerful modeling flexibility, but they require engineering overhead. Yapamar offers a high-speed, AI-powered, productized alternative.',
    author: 'Julia Vasilieva',
    date: 'Oct 14, 2025',
    image: ArticleBgFive,
    content: RobynVsGoogleMeridianVsYapamarContent,
  },
  {
    id: 6,
    slug: 'why-mmm-must-become-default',
    title:
      'Why MMM Must Become the Default Language of Commercial Decision Making',
    description:
      'Commercial leaders today are operating in conditions defined by uncertainty: fragmented media, volatile demand, rising acquisition costs, and intense scrutiny on profitability.',
    author: 'Julia Vasilieva',
    date: 'Dec 19, 2025',
    image: ArticleBgSix,
    content: WhyMMMMustBecomeDefaultContent,
  },
];

// Utility function to find blog post by slug
export const getBlogPostBySlug = (slug: string): BlogPost | undefined => {
  return blogPosts.find((post) => post.slug === slug);
};

// Mock data for the Blog page (simplified for display)
export const blogArticles = blogPosts.map((post) => ({
  id: post.id,
  title: post.title,
  description: post.description,
  author: post.author,
  date: post.date,
  image: post.image,
  link: `/blog/${post.slug}`,
}));

export const changelogUpdates = [
  {
    id: 1,
    title: 'AI-powered campaign summaries',
    description:
      'Generate instant summaries of campaign performance with one click.',
    date: 'Aug 13, 2025',
    image: '/api/placeholder/400/200/2C3865/FFFFFF?text=AI+Summaries',
  },
  {
    id: 2,
    title: 'Customizable dashboard themes',
    description: 'Switch between light, dark, and brand-colored dashboards.',
    date: 'Aug 01, 2025',
    image: '/api/placeholder/400/200/EAA16F/FFFFFF?text=Dashboard+Themes',
  },
  {
    id: 3,
    title: 'Segment Explorer',
    description:
      'Drill into audiences with new demographic and behavioral filters.',
    date: 'Jul 24, 2025',
    image: '/api/placeholder/400/200/2C3865/FFFFFF?text=Segment+Explorer',
  },
  {
    id: 4,
    title: 'PDF export with branding',
    description: 'Download reports with your company logo and colors.',
    date: 'Jul 12, 2025',
    image: '/api/placeholder/400/200/EAA16F/FFFFFF?text=PDF+Branding',
  },
];

export const additionalUpdates = [
  {
    id: 1,
    title: 'Why visualization matters in analytics',
    description:
      'The design principles behind our charts and why they make insights easier to understand.',
    author: 'David Schuster',
    date: 'Jul 10, 2025',
    image: '/api/placeholder/400/200/2C3865/FFFFFF?text=Visualization',
  },
  {
    id: 2,
    title: 'Campaign planning with simulations',
    description:
      'Learn how scenario planning helps teams predict budgets and performance with confidence.',
    author: 'Lisa Romano',
    date: 'Jun 28, 2025',
    image: '/api/placeholder/400/200/EAA16F/FFFFFF?text=Simulations',
  },
  {
    id: 3,
    title: 'User-centric dashboards for agile teams',
    description:
      'How we designed flexible layouts that adapt to different workflows and industries.',
    author: 'Thomas Lindberg',
    date: 'Jun 12, 2025',
    image: '/api/placeholder/400/200/2C3865/FFFFFF?text=User+Dashboards',
  },
  {
    id: 4,
    title: 'The hidden cost of bad metrics',
    description:
      'What happens when analytics mislead, and how Yapamar ensures accuracy.',
    author: 'Katarina Rossi',
    date: 'May 25, 2025',
    image: '/api/placeholder/400/200/EAA16F/FFFFFF?text=Bad+Metrics',
  },
];

export const customerStories = [
  {
    id: 1,
    title: 'Yapamar raises $15M to scale AI-driven marketing analytics',
    description: '',
    company: 'TechCrunch',
    date: 'Jul 25, 2025',
    image: '/api/placeholder/400/200/2C3865/FFFFFF?text=BAM+Case',
  },
  {
    id: 2,
    title: 'How Yapamar helps marketers cut through data noise',
    description: '',
    company: 'Forbes',
    date: 'Jun 30, 2025',
    image: '/api/placeholder/400/200/EAA16F/FFFFFF?text=Cut+Noise',
  },
  {
    id: 3,
    title: 'The new wave of AI analytics platforms: Yapamar leads the charge',
    description: '',
    company: 'AI Business',
    date: 'Jun 10, 2025',
    image: '/api/placeholder/400/200/2C3865/FFFFFF?text=Analytics+Wave',
  },
];

export const additionalCustomerStories = [
  {
    id: 1,
    description:
      'How GreenBank improved conversion by 28% with predictive insights.',
    company: '',
    image: '/api/placeholder/400/200/2C3865/FFFFFF?text=LOGO',
  },
  {
    id: 2,
    description:
      'How an online school used simulations to optimize ad budgets.',
    company: '',
    image: '/api/placeholder/400/200/EAA16F/FFFFFF?text=LOGO',
  },
  {
    id: 3,
    description:
      'Cutting reporting time from days to minutes with automated dashboards.',
    company: '',
  },
];

// Getting Started Guide Steps
export const GETTING_STARTED_STEPS = [
  {
    number: '01',
    title: 'CREATE THE ACCOUNT',
    subtitle: 'Start your journey in a few minutes',
    image: createAccountScreenshot,
    description:
      'Creating an account is the first step to unlocking the full power of Yapamar. The setup process is quick and designed to get you into the platform without unnecessary friction.',
    bulletPoints: [
      'Contact sales to obtain onboarding link',
      'Choose a subscription plan that matches your needs and provide your company details.',
      'Verify your email address - this activates your workspace and keeps it secure.',
      'By default, you’ll be added as the workspace admin, which means you’ll have full control over users, channels, and data access.',
    ],
    tip: 'Use your official company name and a shared inbox for admin access. This will ensure smooth handover if roles change in the future.',
  },
  {
    number: '02',
    title: 'CONNECT CHANNELS',
    subtitle: 'Link ad and analytics sources',
    image: connectChannelsScreenshot,
    description:
      'The more channels you connect, the more complete your marketing performance picture becomes. Yapamar integrates seamlessly with leading ad and analytics platforms.',
    bulletPoints: [
      'Connect your advertising and analytics accounts in just a few clicks.',
      'Secure OAuth login keeps your credentials safe; you can revoke or refresh access anytime in Settings.',
      'Only the minimum permissions required for reporting are requested, so you stay in full control.',
    ],
    note: 'Can’t find the connector you need? You can start by uploading data manually via CSV and request additional integrations through support.',
  },
  {
    number: '03',
    title: 'UPLOAD DATA',
    subtitle: 'Import your historical performance',
    image: uploadDataScreenshot,
    description:
      'To analyze long-term performance and train accurate models, you’ll need to import past data. Yapamar makes this easy with simple templates and validation.',
    bulletPoints: [
      'Use our standardized CSV/XLSX templates (files up to 100 MB are supported).',
      'Drag and drop your files - the system automatically validates format and required fields.',
      'Once uploaded, click **Analyze** to preview rows, metrics, and the date range covered, ensuring your data is ready for modeling.',
    ],
    goodToKnow:
      'If you re-upload updated files, Yapamar automatically de-duplicates records based on keys like campaign ID and date.',
  },
  {
    number: '04',
    title: 'CAMPAIGN MAPPING',
    subtitle: 'Align naming across platforms',
    image: campaignMappingScreenshot,
    description:
      'Different platforms often use inconsistent naming for campaigns and ad sets. Campaign mapping brings everything together into a unified structure.',
    bulletPoints: [
      'Match campaigns with flexible rules.',
      'Resolve duplicates, missing names, or "N/A" fields with one click for clean and reliable data.',
      "Save your mappings as reusable presets, so you don't have to repeat the process for every new dataset.",
    ],
    recommendation:
      'Define a naming convention early to make reporting easier.',
  },
  {
    number: '05',
    title: 'START MODELING',
    subtitle: 'Select KPIs and run your first model',
    image: startModelingScreenshot,
    description:
      "Now it's time to transform your connected data into actionable insights. Modeling lets you evaluate the impact of each channel and identify the best next steps.",
    bulletPoints: [
      'Define your KPI by choosing the source, metric, and category most relevant to your campaign goals.',
      'Select the scope: choose which media channels, campaigns, or ad groups you want to include in the analysis.',
      'Run the model to generate contribution insights and optimization suggestions.',
    ],
    outcome:
      'Your first model will show you how each channel contributes to results and suggest data-driven next-best actions for scaling campaigns.',
  },
];

// Invite Team Steps
export const INVITE_TEAM_STEPS = [
  {
    number: '01',
    title: 'INVITE YOUR FIRST TEAM MEMBER',
    subtitle: 'Get started by adding your first teammate',
    image: inviteFirstMemberScreenshot,
    description:
      'Collaboration starts by adding colleagues to your workspace. In the invitation field, type the email address of the teammate you want to invite. Sending an invite gives them secure access to the same dashboards and data you’re already working with.',
    bulletPoints: [],
    goodToKnow:
      'Once invited, they’ll receive an email with a link to join. You can manage roles and permissions later in Workspace Settings to ensure everyone has the right level of access.',
  },
  {
    number: '02',
    title: 'INVITE YOUR NEXT TEAM MEMBER',
    subtitle: 'Expand your team and collaborate at scale',
    image: inviteNextMemberScreenshot,
    description:
      'After sending your first invitation, you can continue adding more colleagues. Simply type the next email address and send another invite. This way you can quickly bring in your whole team without leaving the page.',
    bulletPoints: [],
    goodToKnow:
      'As more teammates join, you’ll collaborate in real time - sharing insights, tracking results, and aligning on campaign decisions.',
  },
];

// Track Issues Steps
export const TRACK_ISSUES_STEPS = [
  {
    number: '01',
    title: 'CREATE YOUR FIRST GOAL',
    subtitle: 'Set a clear starting point for your team',
    image: createFirstGoalScreenshot,
    description:
      'Start by creating your first goal to begin monitoring progress. Goals make it easier to keep your team aligned and highlight what success looks like.',
    bulletPoints: [],
    tip: 'Click **Add New Goal** to set a target, define deadlines, and assign responsibility. Once added, the goal will appear in the tracking list and provide a baseline for measuring progress.',
  },
  {
    number: '02',
    title: 'VIEW ALL YOUR GOALS',
    subtitle: 'Keep every target visible in one place',
    image: goalsAndTargetsScreenshot,
    description:
      'As you add more goals, they’ll be displayed in a centralized list under Goals & Tracking. Each goal shows:',
    bulletPoints: [
      'Status (On Track, Completed)',
      'Deadlines and pacing',
      'Assigned users',
      'Mentions and comments',
    ],
    outcome:
      'This gives you a quick overview of what’s progressing well and where attention is needed.',
  },
  {
    number: '03',
    title: 'REVIEW GOAL DETAILS',
    subtitle: 'Dive deeper into metrics, status, and collaboration',
    image: goalDetailsScreenshot,
    description: 'Click on any goal to open its detail view. Here you can:',
    bulletPoints: [
      'Review the description and key metrics',
      'Track deadlines and completion percentage',
      'Mention teammates with @username to collaborate',
      'Add comments or context to keep everyone aligned',
      'Update the status to reflect current progress',
    ],
    outcome:
      'By keeping goals updated and linked to your team’s work, everyone gains full visibility into progress, can spot risks early, and stay aligned on shared targets.',
  },
];

// Workspace Settings Sections
export const WORKSPACE_SETTINGS_SECTIONS = [
  {
    number: '1',
    title: 'MANAGE YOUR PROFILE AND ACCOUNT SECURITY',
    subtitle: 'Keep your personal details accurate and ensure secure access',
    image: manageProfileAndAccountSecurityScreenshot,
    description:
      'Your profile is where you control the personal details linked to your Yapamar account. Keeping this information current helps both with security and with smooth collaboration across your team.',
    bulletPoints: [
      'Upload or change your profile photo so teammates can easily recognize you',
      'Edit your username and display name to keep them consistent with company conventions',
      'Update your email address if you switch to a new inbox',
      'Change your password regularly to maintain account security',
    ],
    tip: 'Use a professional email and recognizable photo – this makes teamwork smoother and communication cleaner.',
  },
  {
    number: '2',
    title: 'BILLING AND SUBSCRIPTION MANAGEMENT',
    subtitle: 'Stay on top of your plan, payments, and invoices',
    image: billingAndSubscriptionManagementScreenshot,
    description:
      'Billing settings give you full visibility and control over your plan and costs. Here you can check what you’re paying for, update payment methods, and download invoices.',
    bulletPoints: [
      'View your current plan and monthly or annual cost',
      'Upgrade or downgrade your subscription depending on your needs',
      'Update your payment method securely',
      'Review billing history with downloadable invoices',
      'Monitor upcoming renewals to avoid interruptions',
    ],
    tip: 'Always keep your billing contact information current, so invoices and renewal reminders reach the right person.',
  },
  {
    number: '3',
    title: 'GOAL SETUP AND TRACKING',
    subtitle:
      'Goal setup allows you to define targets that match your company’s priorities. By adding goals directly in your workspace, you ensure your team is focused on the right objectives and has clear metrics to measure success.',
    image: goalSetupAndTrackingScreenshot,
    description:
      'Goal setup lets you define targets that match your company priorities.',
    bulletPoints: [
      'Create new goals that reflect your business strategy',
      'Add targets and link them to specific campaigns or channels',
      'Use consistent naming conventions to simplify tracking',
      'Review and update goals regularly to keep them relevant',
      'Monitor progress to see whether targets are being met',
    ],
    tip: 'Start with a few high-impact goals and expand over time – this keeps reporting simple while still giving a clear picture of performance.',
  },
];

// Custom Setup Sections
export const CUSTOM_SETUP_SECTIONS = [
  {
    title: 'NOTIFICATIONS & ALERTS',
    subtitle: 'Notifications keep your team aligned without overwhelming them',
    image: notificationsSettingsScreenshot,
    description:
      'Notifications keep your team aligned without overwhelming them.',
    bulletPoints: [
      'Choose how you want to receive alerts: in-app, by email, or via different integration',
      'Select which events trigger updates: data uploads, campaign status changes, or when a teammate mentions you',
      'Fine-tune frequency settings to avoid noise and focus only on critical updates',
    ],
    tip: "Start with the default setup, then refine as your workflows evolve. This way you won't miss important changes but also won't get overloaded.",
  },
  {
    title: 'INTEGRATIONS OVERVIEW',
    subtitle: 'Manage connected platforms in one place',
    image: integrationsManagementScreenshot,
    description:
      'Keep track of all your ad and analytics integrations from a single view. This helps ensure data flows smoothly and lets you control connections without digging through multiple settings.',
    bulletPoints: [
      'See a list of all active ad & analytics connections',
      'Reconnect or refresh access tokens securely when they expire',
      'Disconnect platforms you no longer use to keep your workspace clean',
      'Request additional integrations directly via support',
    ],
    tip: 'Regularly review this page to confirm all tokens are valid - expired connections can interrupt data flow without warning.',
  },
  {
    title: 'AI INSIGHTS SETTINGS',
    subtitle: 'Customize how Gen AI supports your analysis',
    image: aiInsightsDashboardScreenshot,
    description:
      'Yapamar automatically analyzes your connected channels and generates insights to help you make better decisions.',
    bulletPoints: [
      'See highlights of anomalies, pacing trends, and optimization opportunities',
      'Receive clear summaries directly in your dashboards',
      'Use insights to identify risks early and act on opportunities faster',
      'Share generated insights with your team to align decisions',
    ],
    tip: 'Treat AI insights as a starting point - they surface patterns quickly, while your team adds context and decides on next steps.',
  },
];

// How Integrations Work Sections
export const INTEGRATIONS_SECTIONS = [
  {
    number: '1',
    title: 'MANAGE CONNECTED PLATFORMS',
    subtitle:
      'View, refresh, or disconnect your ad and analytics integrations.',
    image: manageConnectedPlatformsScreenshot,
    description:
      'Integrations let you bring data from all your ad and analytics platforms into one workspace. Instead of logging into each tool separately, Yapamar unifies them so you can track performance consistently.',
    bulletPoints: [
      'View all active connections in one place',
      'Check connection status and refresh tokens when needed',
      'Disconnect platforms you no longer use to keep your workspace clean',
      'Request new integrations via support if something is missing',
    ],
    tip: 'Regularly review your integrations page to make sure tokens are valid and all the right channels are connected. This avoids interruptions in your data flow.',
  },
];

// FAQ & Support Data
export const FAQ_CATEGORIES = [
  'All',
  'Getting Started',
  'Integrations',
  'Data & Uploads',
  'Modeling & Insights',
  'Workspace Settings',
  'Billing & Plans',
];

export const FAQ_DATA = [
  {
    id: 1,
    question: 'How do I create my first account?',
    category: 'Getting Started',
    answer: `Creating an account is the first step to unlocking the full power of Yapamar. Go to "Create Account", provide your company details, verify your email address, and you'll become the workspace admin with full control over users, channels, and data access.`,
  },
  {
    id: 2,
    question: 'Can I connect multiple ad platforms?',
    category: 'Integrations',
    answer: `Yes! Yapamar integrates seamlessly with leading ad and analytics platforms. You can connect multiple advertising and analytics accounts in just a few clicks using secure OAuth login. Only the minimum permissions required for reporting are requested, so you stay in full control.`,
  },
  {
    id: 3,
    question: 'How do I upload past campaign data?',
    category: 'Data & Uploads',
    answer: `Use our standardized CSV/XLSX templates (files up to 100 MB are supported). Simply drag and drop your files - the system automatically validates format and required fields. Once uploaded, click "Analyze" to preview rows, metrics, and the date range covered.`,
  },
  {
    id: 4,
    question: 'What if my campaigns have different names across platforms?',
    category: 'Modeling & Insights',
    answer: `Campaign mapping brings everything together into a unified structure. You can match campaigns with flexible rules and resolve duplicates, missing names, or "N/A" fields with one click. Save your mappings as reusable presets for future use.`,
  },
  {
    id: 5,
    question: 'Where can I manage workspace roles?',
    category: 'Workspace Settings',
    answer: `You can manage workspace roles in the Workspace Settings section. Here you can invite team members, assign different permission levels, and control access to specific features and data.`,
  },
  {
    id: 6,
    question: 'How does billing work and what payment options are supported?',
    category: 'Billing & Plans',
    answer: `Yapamar uses a subscription-based billing model with monthly or annual options. Invoices are generated automatically and can be downloaded from the "Billing" section. We support credit/debit cards (Visa, Mastercard, Amex) and for enterprise accounts, wire transfers or custom invoicing. You can upgrade, downgrade, or cancel subscriptions anytime.`,
  },
  {
    id: 7,
    question: 'How do I invite team members to my workspace?',
    category: 'Getting Started',
    answer: `Go to your workspace settings and click "Invite Members". Enter the email addresses of your teammates and assign appropriate roles. They'll receive an email invitation to join your workspace.`,
  },
  {
    id: 8,
    question: 'What integrations are currently available?',
    category: 'Integrations',
    answer: `We support integrations with major ad platforms like Google Ads, Facebook Ads, LinkedIn Ads, and analytics platforms like Google Analytics. If you need a specific integration, you can request it through our support team.`,
  },
  {
    id: 9,
    question: 'How do I set up goals and track progress?',
    category: 'Workspace Settings',
    answer: `In the Goals & Tracking section, you can create new goals that reflect your business strategy. Add targets and link them to specific campaigns or channels. Monitor progress to see whether targets are being met and adjust as needed.`,
  },
  {
    id: 10,
    question: 'What file formats are supported for data uploads?',
    category: 'Data & Uploads',
    answer: `We support CSV and XLSX file formats for data uploads. Files can be up to 100 MB in size. Make sure to use our standardized templates for best results and automatic validation.`,
  },
];
